/**
 * Payment forecast movement log.
 *
 * Every time the projected payment schedule can move — an anchor milestone is
 * rescheduled, a change order is approved, or the contract is signed — a
 * snapshot of the full installment forecast is appended to
 * `timberProjects/{projectId}/paymentForecastLog`. The CFO finance agent
 * replays this history to compare projected collection dates/amounts against
 * actual cash received.
 *
 * Writes are best-effort and deduplicated: identical consecutive forecasts
 * (same amounts + due dates) are skipped, so callers can invoke this after any
 * milestone write without spamming the log.
 */

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ChangeOrder, Milestone, ProjectTask, ProposalBuilderData, TaskGroup, TimberProject } from '../types';
import { getSignedCommercialListTotal } from './proposalPublicGrandTotals';
import { buildPaymentScheduleBreakdown } from './paymentSchedule';
import {
  computePassedInstallments,
  computePaymentDueForecast,
  resolvePaymentDueTaskAnchors,
  toDateSafe,
  DEFAULT_PAYMENT_LAG_SETTINGS,
  type PaymentDueFlag,
} from './paymentDueFlags';
import { parsePaymentLagSettings, PAYMENT_TERMS_SETTINGS_DOC_ID } from './paymentTermsSettings';
import { mirrorAllOnboardingTasks } from './onboardingTaskBridge';
import type { OnboardingItem } from '../types';

/** Raw `projectOnboarding` items store Timestamps; the task bridge expects Dates. */
function parseOnboardingItems(raw: unknown): OnboardingItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((it: Record<string, unknown>) => ({
    ...(it as unknown as OnboardingItem),
    startDate: toDateSafe(it.startDate as Date | { toDate?: () => Date } | undefined) ?? undefined,
    endDate: toDateSafe(it.endDate as Date | { toDate?: () => Date } | undefined) ?? undefined,
    actualStartDate:
      toDateSafe(it.actualStartDate as Date | { toDate?: () => Date } | undefined) ?? undefined,
    actualEndDate:
      toDateSafe(it.actualEndDate as Date | { toDate?: () => Date } | undefined) ?? undefined,
    completedAt:
      toDateSafe(it.completedAt as Date | { toDate?: () => Date } | undefined) ?? undefined,
    lockedAt: toDateSafe(it.lockedAt as Date | { toDate?: () => Date } | undefined) ?? undefined,
  }));
}

export type PaymentForecastTrigger = 'milestone_moved' | 'co_approved' | 'schedule_created';

export interface PaymentForecastInstallmentSnapshot {
  installmentId: string;
  kind: 'installment' | 'change_order';
  label: string;
  detail: string;
  amount: number;
  dueDate: Timestamp | null;
  anchorMilestoneId?: string;
  anchorMilestoneTitle?: string;
  estimated: boolean;
}

export interface PaymentForecastLogEntry {
  at: Timestamp;
  byUid: string;
  trigger: PaymentForecastTrigger;
  installments: PaymentForecastInstallmentSnapshot[];
  contractValue: number;
  baseTotal: number;
  /** CO that caused the snapshot (trigger 'co_approved'). */
  changeOrderId?: string;
  /** Milestone that caused the snapshot (trigger 'milestone_moved'). */
  milestoneId?: string;
}

export const PAYMENT_FORECAST_LOG_SUBCOLLECTION = 'paymentForecastLog';

function flagToSnapshot(f: PaymentDueFlag): PaymentForecastInstallmentSnapshot {
  const snap: PaymentForecastInstallmentSnapshot = {
    installmentId: f.installmentId,
    kind: f.kind,
    label: f.label,
    detail: f.detail,
    amount: f.amount,
    dueDate: f.dueDate ? Timestamp.fromDate(f.dueDate) : null,
    estimated: f.estimated,
  };
  if (f.anchorMilestoneId) snap.anchorMilestoneId = f.anchorMilestoneId;
  if (f.anchorMilestoneTitle) snap.anchorMilestoneTitle = f.anchorMilestoneTitle;
  return snap;
}

function forecastFingerprint(installments: PaymentForecastInstallmentSnapshot[]): string {
  return JSON.stringify(
    installments.map(i => [i.installmentId, i.amount, i.dueDate ? i.dueDate.toMillis() : null]),
  );
}

export interface RecordPaymentForecastOptions {
  byUid?: string;
  changeOrderId?: string;
  milestoneId?: string;
}

/**
 * Compute the current payment forecast for a project and append it to the log
 * (skipping when unchanged). No-ops when the proposal is not signed. Never
 * throws — failures only log a warning so schedule/approval flows can't break.
 */
export async function recordPaymentForecastSnapshot(
  projectId: string,
  trigger: PaymentForecastTrigger,
  options?: RecordPaymentForecastOptions,
): Promise<void> {
  if (!projectId) return;
  try {
    const projSnap = await getDoc(doc(db, 'timberProjects', projectId));
    if (!projSnap.exists()) return;
    const project = projSnap.data() as TimberProject;
    const dealId = project.dealId;
    if (!dealId) return;

    // Signed base — no forecast to track before signing.
    const propSnap = await getDoc(doc(db, 'proposalBuilders', dealId));
    if (!propSnap.exists()) return;
    const signedBase = getSignedCommercialListTotal(propSnap.data() as ProposalBuilderData);
    if (signedBase == null || signedBase <= 0) return;

    const [coSnap, msSnap, taskSnap, groupSnap, lagSnap, onboardingSnap] = await Promise.all([
      getDocs(query(collection(db, 'changeOrders'), where('dealId', '==', dealId))),
      getDocs(query(collection(db, 'milestones'), where('projectId', '==', projectId))),
      getDocs(query(collection(db, 'projectTasks'), where('projectId', '==', projectId))),
      getDocs(query(collection(db, 'taskGroups'), where('projectId', '==', projectId))),
      getDoc(doc(db, 'financeSettings', PAYMENT_TERMS_SETTINGS_DOC_ID)).catch(() => null),
      getDoc(doc(db, 'projectOnboarding', projectId)).catch(() => null),
    ]);
    const lagSettings = lagSnap?.exists()
      ? parsePaymentLagSettings(lagSnap.data())
      : { ...DEFAULT_PAYMENT_LAG_SETTINGS };
    const changeOrders = coSnap.docs.map(d => ({ id: d.id, ...(d.data() as ChangeOrder) }));
    const milestones = msSnap.docs.map(d => ({ id: d.id, ...(d.data() as Milestone) }));
    const taskGroups = groupSnap.docs.map(d => ({ id: d.id, ...(d.data() as TaskGroup) }));
    // Merge onboarding-checklist mirror tasks — often the only members of the
    // Production group, which anchors the 40% installment.
    const onboardingTasks = onboardingSnap?.exists()
      ? mirrorAllOnboardingTasks(
          [{ projectId, items: parseOnboardingItems(onboardingSnap.data()?.items) }],
          taskGroups,
        )
      : [];
    const projectTasks = [
      ...taskSnap.docs.map(d => ({ id: d.id, ...(d.data() as ProjectTask) })),
      ...onboardingTasks,
    ];

    const projectDates = {
      contractSignedAt: project.contractedAt ?? project.startDate,
      startDate: project.startDate,
      targetEndDate: project.targetEndDate,
    };
    const taskAnchors = resolvePaymentDueTaskAnchors(projectId, projectTasks, taskGroups);
    const lastProductionTaskEnd = taskAnchors.production?.date ?? null;
    const schedule = buildPaymentScheduleBreakdown(signedBase, changeOrders, {
      passedInstallmentIds: computePassedInstallments(
        projectDates,
        milestones,
        undefined,
        lastProductionTaskEnd,
        taskAnchors.install?.date ?? taskAnchors.namedFinal?.date ?? null,
      ),
    });
    const approvedAtById: Record<string, Date | null> = {};
    changeOrders.forEach(co => {
      approvedAtById[`co-${co.id || co.number}`] = toDateSafe(
        co.approvedAt as Date | { toDate?: () => Date } | undefined,
      );
    });
    const forecast = computePaymentDueForecast(
      projectDates,
      milestones,
      schedule,
      approvedAtById,
      lastProductionTaskEnd,
      lagSettings,
      taskAnchors.project?.date ?? null,
      {
        production: taskAnchors.production?.taskTitle,
        project: taskAnchors.project?.taskTitle,
        install: taskAnchors.install?.taskTitle,
      },
      taskAnchors,
    );
    const installments = forecast.map(flagToSnapshot);

    // Dedupe against the latest snapshot.
    const logCol = collection(db, 'timberProjects', projectId, PAYMENT_FORECAST_LOG_SUBCOLLECTION);
    try {
      const lastSnap = await getDocs(query(logCol, orderBy('at', 'desc'), limit(1)));
      if (!lastSnap.empty) {
        const last = lastSnap.docs[0].data() as PaymentForecastLogEntry;
        if (forecastFingerprint(last.installments || []) === forecastFingerprint(installments)) {
          return;
        }
      }
    } catch {
      /* dedupe is best-effort */
    }

    const entry: PaymentForecastLogEntry = {
      at: Timestamp.now(),
      byUid: options?.byUid || 'system',
      trigger,
      installments,
      contractValue: schedule.currentContractValue,
      baseTotal: schedule.baseTotal,
    };
    if (options?.changeOrderId) entry.changeOrderId = options.changeOrderId;
    if (options?.milestoneId) entry.milestoneId = options.milestoneId;

    await addDoc(logCol, entry);
  } catch (err) {
    console.warn('[paymentForecastLog] snapshot failed for project', projectId, trigger, err);
  }
}

/**
 * Snapshot every project attached to a deal (CO approval / signing flows know
 * the deal, not the project).
 */
export async function recordPaymentForecastSnapshotForDeal(
  dealId: string,
  trigger: PaymentForecastTrigger,
  options?: RecordPaymentForecastOptions,
): Promise<void> {
  if (!dealId) return;
  try {
    const tq = await getDocs(query(collection(db, 'timberProjects'), where('dealId', '==', dealId)));
    await Promise.all(
      tq.docs.map(d => recordPaymentForecastSnapshot(d.id, trigger, options)),
    );
  } catch (err) {
    console.warn('[paymentForecastLog] deal snapshot failed for', dealId, trigger, err);
  }
}
